export enum CookieCategories {
  Essential = 'essentialCookies',
  Tracking = 'trackingCookies',
  Recording = 'recordingCookies',
}

interface CookieSettings<T> {
  name: string;
  exists: boolean;
  expires?: number;
  data: T;
}

interface CookieManagement {
  [CookieCategories.Tracking]: boolean;
  [CookieCategories.Recording]: boolean;
  version: string;
}

const FULL_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// https://dev.azure.com/tempcover/tempcover_dev/_wiki/wikis/tempcover_dev.wiki/598/Cookie-Version-History
export const CookieManagementCookieSettings = {
  name: 'tcCookieManagement',
  length: FULL_YEAR_MS,
  version: '1.4.0',
} as const;

export const DisplayCookieBannerCookieSettings = {
  name: 'displayCookieBanner',
} as const;

export const FbcCookieSettings = {
  name: '_fbc',
  maxAge: 90 * 24 * 60 * 60, // 90 days in seconds
  // regex to extract fbclid from cookie value (format: fb.1.timestamp.fbclid)
  valueRegex: /^fb\.1\.\d+\.(.*)$/,
} as const;

const defaultCookieManagementSettings: CookieSettings<CookieManagement> = {
  name: CookieManagementCookieSettings.name,
  exists: false,
  data: {
    trackingCookies: false,
    recordingCookies: false,
    version: CookieManagementCookieSettings.version,
  },
};

export const getCookieManagementSettings = async (cookieValue?: string): Promise<CookieSettings<CookieManagement>> => {
  if (!cookieValue) return defaultCookieManagementSettings;

  try {
    const preferences: CookieManagement = JSON.parse(cookieValue);

    if (preferences.version !== CookieManagementCookieSettings.version) {
      return defaultCookieManagementSettings;
    }

    return {
      name: CookieManagementCookieSettings.name,
      exists: true,
      data: {
        ...preferences,
      },
    };
  } catch {
    return defaultCookieManagementSettings;
  }
};

export const createCookieManagementSettings = async (
  cookieFormData: FormData
): Promise<CookieSettings<CookieManagement>> => {
  const trackingCookies = cookieFormData.get(CookieCategories.Tracking)?.toString().toLowerCase() === 'true';
  const recordingCookies = cookieFormData.get(CookieCategories.Recording)?.toString().toLowerCase() === 'true';
  const now = new Date();
  const expires = now.setTime(now.getTime() + CookieManagementCookieSettings.length);

  return {
    name: CookieManagementCookieSettings.name,
    exists: false,
    expires,
    data: {
      trackingCookies,
      recordingCookies,
      version: CookieManagementCookieSettings.version,
    },
  };
};
